import "@fontsource-variable/plus-jakarta-sans";

import { BrandingProvider, useBranding } from "@app-setup:BrandingContext";
import { LanguageSync } from "@app-setup:LanguageSync";
import { routes } from "@app-setup:routes";
import { ErrorPage } from "@components:ErrorPage";
import { LoadingScreen } from "@components:LoadingScreen";
import { ScrollToTopButton } from "@components:ScrollToTopButton";
import { Toaster } from "@components:Toaster";
import { queryClient } from "@libs:react-query";
import { AuthLoader } from "@libs:react-query-auth";
import { CssBaseline, ThemeProvider } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterLuxon } from "@mui/x-date-pickers/AdapterLuxon";
import { QueryClientProvider } from "@tanstack/react-query";
import * as React from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useTranslation } from "react-i18next";
import { RouterProvider } from "react-router";

import { createBrandedTheme, themes } from "@:themes";

// Inner component that uses branding context to create dynamic theme
const BrandedThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const { config } = useBranding();

  const theme = React.useMemo(() => {
    if (
      config?.primaryColor ||
      config?.secondaryColor ||
      config?.sidebarColor ||
      config?.backgroundColor
    ) {
      return createBrandedTheme(
        config.primaryColor,
        config.secondaryColor,
        config.sidebarColor,
        config.backgroundColor,
      );
    }
    return themes;
  }, [
    config?.primaryColor,
    config?.secondaryColor,
    config?.sidebarColor,
    config?.backgroundColor,
  ]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      {children}
    </ThemeProvider>
  );
};

// Wrapper for pre-auth loading state (uses default theme)
const PreAuthThemeProvider = ({ children }: { children: React.ReactNode }) => (
  <ThemeProvider theme={themes}>
    <CssBaseline enableColorScheme />
    {children}
  </ThemeProvider>
);

// Feeds the active language to the date pickers so dates render localised
const LocalizedDateProvider = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();

  return (
    <LocalizationProvider
      dateAdapter={AdapterLuxon}
      adapterLocale={i18n.language}
    >
      {children}
    </LocalizationProvider>
  );
};

export const AppProvider = () => {
  return (
    <React.Suspense fallback={<LoadingScreen />}>
      <LocalizedDateProvider>
        <ErrorBoundary FallbackComponent={ErrorPage}>
          <QueryClientProvider client={queryClient}>
            <PreAuthThemeProvider>
              <AuthLoader renderLoading={() => <LoadingScreen />}>
                <BrandingProvider>
                  <BrandedThemeProvider>
                    <LanguageSync />
                    <RouterProvider router={routes} />
                    <ScrollToTopButton />
                    <Toaster />
                  </BrandedThemeProvider>
                </BrandingProvider>
              </AuthLoader>
            </PreAuthThemeProvider>
          </QueryClientProvider>
        </ErrorBoundary>
      </LocalizedDateProvider>
    </React.Suspense>
  );
};
